
import React, { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSettings } from "@/context/SettingsContext";
import { cn } from "@/lib/utils";
import { Focus } from "lucide-react";
import { lensFocusPrompts } from "@/data/journalPrompts";

interface LensFocusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectLens: (lensType: string) => void;
}

const LensFocusDialog: React.FC<LensFocusDialogProps> = ({ 
  open, 
  onOpenChange,
  onSelectLens
}) => {
  const { darkMode } = useSettings();
  
  const lensOptions = [
    {
      id: "1minute",
      title: lensFocusPrompts["1minute"].title,
      description: lensFocusPrompts["1minute"].prompt,
      icon: <Focus className="w-5 h-5" />
    },
    {
      id: "location",
      title: lensFocusPrompts["location"].title,
      description: lensFocusPrompts["location"].prompt,
      icon: <Focus className="w-5 h-5" />
    },
    {
      id: "sensory",
      title: lensFocusPrompts["sensory"].title,
      description: lensFocusPrompts["sensory"].prompt,
      icon: <Focus className="w-5 h-5" />
    },
    {
      id: "emotional",
      title: lensFocusPrompts["emotional"].title,
      description: lensFocusPrompts["emotional"].prompt,
      icon: <Focus className="w-5 h-5" />
    }
  ];

  // Automatically select a random lens when the dialog opens
  useEffect(() => {
    if (open) {
      const randomIndex = Math.floor(Math.random() * lensOptions.length);
      const selectedLens = lensOptions[randomIndex];
      
      // Use a small timeout to ensure the dialog is visible first
      // This prevents the immediate closing issue
      const timer = setTimeout(() => {
        onSelectLens(selectedLens.id);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [open, onSelectLens, lensOptions]);

  // Get the selected lens for display
  const selectedLensIndex = Math.floor(Math.random() * lensOptions.length);
  const selectedLens = lensOptions[selectedLensIndex];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "sm:max-w-md",
        darkMode ? "bg-[#2d1a45] text-[#FBF3F1] border-[#6127A0]/30" : ""
      )}>
        <DialogHeader>
          <DialogTitle className={darkMode ? "text-[#00FF99]" : "text-[#1E0C46]"}>
            Turn on Untangle Mode
          </DialogTitle>
          <DialogDescription className={darkMode ? "text-[#FBF3F1]/80" : "text-gray-500"}>
            Too Big to Begin? Add an extra reflection that makes it easier to take action.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 flex justify-center items-center">
          <div className={cn(
            "p-6 rounded-lg border flex items-center",
            darkMode 
              ? "border-[#6127A0] bg-[#6127A0]/20" 
              : "border-[#D896A4]/30 bg-[#6127A0]/10"
          )}>
            <div className={cn(
              "mr-4 p-3 rounded-full",
              darkMode ? "bg-[#6127A0]/30" : "bg-[#6127A0]/20"
            )}>
              {selectedLens.icon}
            </div>
            <div>
              <h3 className={cn(
                "font-medium mb-1",
                darkMode ? "text-[#00FF99]" : "text-[#1E0C46]"
              )}>
                {selectedLens.title}
              </h3>
              <p className={cn(
                "text-sm",
                darkMode ? "text-[#FBF3F1]/80" : "text-gray-600"
              )}>
                {selectedLens.description}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LensFocusDialog;
